import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import api, { JOURNEY_TYPES, LANGUAGES } from "@/lib/api";
import Seo from "@/components/common/Seo";
import { JourneyCard } from "@/components/common/Cards";

const ORGANS = ["kidney", "liver", "heart", "lung", "pancreas", "intestine"];
const ORGAN_LABELS = { kidney: "Kidney", liver: "Liver", heart: "Heart", lung: "Lung", pancreas: "Pancreas", intestine: "Intestine" };

export default function Explore() {
  const [params, setParams] = useSearchParams();
  const [states, setStates] = useState([]);
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(true);

  const organ = params.get("organ") || "";
  const state = params.get("state") || "";
  const jtype = params.get("journey_type") || "";
  const language = params.get("language") || "";
  const q = params.get("q") || "";
  const [qInput, setQInput] = useState(q);

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next);
  };

  useEffect(() => { api.get("/states").then((r) => setStates(r.data)); }, []);

  useEffect(() => {
    const t = setTimeout(() => { if (qInput.trim() !== q) update("q", qInput.trim()); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qInput]);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (organ) qs.set("organ", organ);
    if (state) qs.set("state", state);
    if (jtype) qs.set("journey_type", jtype);
    if (language) qs.set("language", language);
    if (q) qs.set("q", q);
    api.get(`/journeys?${qs.toString()}`).then((r) => setJourneys(r.data)).finally(() => setLoading(false));
  }, [organ, state, jtype, language, q]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <Seo title="Explore Journeys — Transplant Story" description="Explore real transplant journeys across India. Filter by organ, state, journey type and language." />
      <h1 className="text-4xl sm:text-5xl font-light tracking-tight text-ink">Explore journeys</h1>

      {/* Organ pills */}
      <div className="flex flex-wrap gap-2 mt-10">
        <FilterPill active={!organ} onClick={() => update("organ", "")} testId="filter-organ-all">All organs</FilterPill>
        {ORGANS.map((o) => (
          <FilterPill key={o} active={organ === o} onClick={() => update("organ", o)} testId={`filter-organ-${o}`}>
            {ORGAN_LABELS[o]}
          </FilterPill>
        ))}
      </div>

      {/* Hospital search + selects */}
      <div className="mt-6 max-w-md flex items-center border border-neutral-300 bg-white px-4 py-2 focus-within:border-rust transition-colors">
        <Search className="w-4 h-4 text-neutral-400 shrink-0" />
        <input
          data-testid="filter-hospital"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
          placeholder="Search by hospital name…"
          className="bg-transparent outline-none text-sm ml-2 flex-1 placeholder:text-neutral-400"
        />
      </div>
      <div className="flex flex-wrap gap-3 mt-4">
        <Select testId="filter-state" value={state} onChange={(v) => update("state", v)} placeholder="All India">
          {states.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
        </Select>
        <Select testId="filter-type" value={jtype} onChange={(v) => update("journey_type", v)} placeholder="Any contributor">
          {JOURNEY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </Select>
        <Select testId="filter-language" value={language} onChange={(v) => update("language", v)} placeholder="Any language">
          {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
        </Select>
      </div>

      {/* Results */}
      <div className="mt-12">
        {loading ? (
          <p className="text-neutral-500">Loading…</p>
        ) : journeys.length === 0 ? (
          <div className="border border-dashed border-neutral-300 p-12 text-center">
            <p className="text-neutral-600">No journeys match these filters yet.</p>
            <p className="text-sm text-neutral-500 mt-2">Transparency is a core principle — some areas have no stories shared yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {journeys.map((j) => <JourneyCard key={j.id} journey={j} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function FilterPill({ active, onClick, children, testId }) {
  return (
    <button data-testid={testId} onClick={onClick}
      className={`px-4 py-2 text-sm rounded-full border transition-colors ${active ? "bg-rust text-white border-rust" : "border-neutral-300 text-neutral-600 hover:border-ink"}`}>
      {children}
    </button>
  );
}

function Select({ value, onChange, placeholder, children, testId }) {
  return (
    <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)}
      className="px-4 py-2 text-sm border border-neutral-300 bg-white text-ink outline-none focus:border-rust transition-colors">
      <option value="">{placeholder}</option>
      {children}
    </select>
  );
}
